import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { PasswordResetRequest } from '../../models/password-reset-request';
import { PasswordResetRequestsService } from '../../services/password-reset-requests/password-reset-requests';

@Component({
  selector: 'app-password-reset-requests',
  standalone: false,
  templateUrl: './password-reset-requests.html',
  styleUrls: ['./password-reset-requests.css']
})
export class PasswordResetRequests implements OnInit {
  requests: PasswordResetRequest[] = [];
  loading = false;

  selectedRequest: PasswordResetRequest | null = null;

  resolveModel = {
    newPassword: '',
    confirmPassword: '',
    resolutionNote: 'Password reset by administrator.'
  };

  rejectNote = '';
  resolving = false;
  rejecting = false;

  lastResetPassword = '';

  constructor(
    private passwordResetRequestsService: PasswordResetRequestsService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadRequests();
  }

  loadRequests(): void {
    this.loading = true;

    this.passwordResetRequestsService.getPending().pipe(
      finalize(() => {
        this.loading = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: response => {
        if (!response.success || !Array.isArray(response.data)) {
          this.requests = [];
          this.toastr.error(response.message || 'Unable to load password reset requests.');
          this.cdr.detectChanges();
          return;
        }

        this.requests = response.data;
        this.cdr.detectChanges();
      },
      error: error => {
        this.requests = [];
        this.toastr.error(
          error?.error?.message ||
            error?.error?.error?.details ||
            'Unable to load password reset requests.',
          'Password Requests'
        );
        this.cdr.detectChanges();
      }
    });
  }

  openResolveModal(request: PasswordResetRequest): void {
    this.selectedRequest = request;
    this.lastResetPassword = '';

    this.resolveModel = {
      newPassword: this.generateTemporaryPassword(),
      confirmPassword: '',
      resolutionNote: 'Password reset by administrator.'
    };

    const modalEl = document.getElementById('resolvePasswordRequestModal');
    const modal = new (window as any).bootstrap.Modal(modalEl);
    modal.show();
  }

  openRejectModal(request: PasswordResetRequest): void {
    this.selectedRequest = request;
    this.rejectNote = '';

    const modalEl = document.getElementById('rejectPasswordRequestModal');
    const modal = new (window as any).bootstrap.Modal(modalEl);
    modal.show();
  }

  resolveRequest(): void {
    if (!this.selectedRequest) return;

    if (!this.resolveModel.newPassword.trim()) {
      this.toastr.warning('New password is required.', 'Validation');
      return;
    }

    if (this.resolveModel.newPassword !== this.resolveModel.confirmPassword) {
      this.toastr.warning('Passwords do not match.', 'Validation');
      return;
    }

    this.resolving = true;

    this.passwordResetRequestsService
      .resolve(
        this.selectedRequest.id,
        this.resolveModel.newPassword,
        this.resolveModel.resolutionNote || null
      )
      .subscribe({
        next: response => {
          this.resolving = false;

          if (!response.success) {
            this.toastr.error(response.message || 'Unable to resolve password request.');
            return;
          }

          this.lastResetPassword = this.resolveModel.newPassword;

          this.toastr.success('Password reset request resolved successfully.');
          this.loadRequests();
        },
        error: error => {
          this.resolving = false;

          this.toastr.error(
            error?.error?.message ||
              error?.error?.error?.details ||
              'Unable to resolve password request.',
            'Password Requests'
          );
        }
      });
  }

  rejectRequest(): void {
    if (!this.selectedRequest) return;

    this.rejecting = true;

    this.passwordResetRequestsService
      .reject(this.selectedRequest.id, this.rejectNote || null)
      .subscribe({
        next: response => {
          this.rejecting = false;

          if (!response.success) {
            this.toastr.error(response.message || 'Unable to reject password request.');
            return;
          }

          this.toastr.success('Password reset request rejected successfully.');

          const modalEl = document.getElementById('rejectPasswordRequestModal');
          const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
          modal?.hide();

          this.loadRequests();
        },
        error: error => {
          this.rejecting = false;

          this.toastr.error(
            error?.error?.message ||
              error?.error?.error?.details ||
              'Unable to reject password request.',
            'Password Requests'
          );
        }
      });
  }

  copyPassword(): void {
    if (!this.lastResetPassword) return;

    navigator.clipboard.writeText(this.lastResetPassword);
    this.toastr.success('Temporary password copied.');
  }

  closeResolveModal(): void {
    const modalEl = document.getElementById('resolvePasswordRequestModal');
    const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
    modal?.hide();

    this.lastResetPassword = '';
  }

  generateTemporaryPassword(): string {
    const random = Math.random().toString(36).slice(-6);
    return `RiskGuard@${random}1`;
  }
}
